import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import DesignTokens from '../../styles/DesignTokens';
import { PSYCHOGRAPHIC_DIMENSIONS, TRAVEL_SOUL_LABELS } from '../../data/psychographicData';

/* ═══════════════════════════════════════════
   RADAR CHART (pure SVG, shared with TravelDNAResult)
   ═══════════════════════════════════════════ */

const DIMENSIONS = [
  { key: 'culture', label: 'Culture', icon: '\u{1F3DB}' },
  { key: 'adventure', label: 'Adventure', icon: '\u{1F3D4}' },
  { key: 'relaxation', label: 'Relaxation', icon: '\u{1F3D6}' },
  { key: 'food', label: 'Food', icon: '\u{1F372}' },
  { key: 'nature', label: 'Nature', icon: '\u{1F33F}' },
  { key: 'social', label: 'Social', icon: '\u{1F37B}' },
];

const CHART_SIZE = 240;
const CENTER = CHART_SIZE / 2;
const RADIUS = 75;
const RINGS = 4;

function polarToCartesian(angle, radius) {
  const rad = (angle - 90) * (Math.PI / 180);
  return {
    x: CENTER + radius * Math.cos(rad),
    y: CENTER + radius * Math.sin(rad),
  };
}

/* ═══════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════ */

const Card = styled(motion.div)`
  background: #ffffff;
  border-radius: 20px;
  padding: 24px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.04);
  color: #1a1a2e;
  margin-bottom: 16px;

  @media (min-width: 769px) {
    padding: 32px;
  }
`;

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 4px 0;

  @media (min-width: 769px) {
    font-size: 20px;
  }
`;

const CardSubtitle = styled.p`
  font-size: 13px;
  color: #94a3b8;
  margin: 0 0 20px 0;
`;

const ChartContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

const PersonalityBadge = styled.div`
  text-align: center;
  margin-bottom: 20px;
`;

const PersonalityLabel = styled.span`
  display: inline-block;
  padding: 6px 16px;
  border-radius: 9999px;
  background: linear-gradient(135deg, #4285F4 0%, #1A73E8 100%);
  color: white;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(66, 133, 244, 0.35);
`;

/* ─── Dimension Bars ─── */

const DimensionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
`;

const DimRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DimIcon = styled.span`
  font-size: 16px;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
`;

const DimLabel = styled.span`
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
  flex-shrink: 0;
`;

const DimTrack = styled.div`
  flex: 1;
  height: 6px;
  background: #e2e8f0;
  border-radius: 9999px;
  overflow: hidden;
  cursor: ${props => props.$editable ? 'pointer' : 'default'};
  position: relative;
`;

const DimFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(135deg, #4285F4 0%, #1A73E8 100%);
  border-radius: 9999px;
`;

const DimValue = styled.span`
  font-size: 11px;
  color: #94a3b8;
  width: 28px;
  flex-shrink: 0;
  text-align: right;
`;

/* ─── Learned Preferences ─── */

const LearnedSection = styled.div`
  padding-top: 16px;
  border-top: 1px solid #f0f0f5;
  margin-bottom: 16px;
`;

const SectionLabel = styled.h3`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #4285F4;
  margin: 0 0 12px 0;
`;

const PreferenceChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 9999px;
  background: #e8f0fe;
  color: #1A73E8;
  font-size: 12px;
  font-weight: 500;
  margin: 0 6px 6px 0;
`;

const AvoidChip = styled(PreferenceChip)`
  background: #fef2f2;
  color: #dc2626;
`;

/* ─── Controls ─── */

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f5;
`;

const ResetBtn = styled.button`
  background: none;
  border: 1px solid #e2e8f0;
  color: #64748b;
  padding: 6px 14px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #cbd5e1;
    color: #1a1a2e;
  }
`;

const SurpriseToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #64748b;
  cursor: pointer;
`;

const ToggleSwitch = styled.div`
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background: ${props => props.$on ? '#4285F4' : '#e2e8f0'};
  position: relative;
  transition: background 0.2s;

  &::after {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: white;
    top: 2px;
    left: ${props => props.$on ? '18px' : '2px'};
    transition: left 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }
`;

/* ─── Travel Soul Badge ─── */

const TravelSoulBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #e8f0fe;
  border-radius: 14px;
  margin-bottom: 20px;
`;

const TravelSoulIcon = styled.span`
  font-size: 24px;
  flex-shrink: 0;
`;

const TravelSoulText = styled.div`
  flex: 1;
  min-width: 0;
`;

const TravelSoulName = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 2px;
`;

const TravelSoulDesc = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
`;

/* ─── Psychographic Spectrum ─── */

const PsychographicSection = styled.div`
  padding-top: 16px;
  border-top: 1px solid #f0f0f5;
  margin-bottom: 16px;
`;

const PsychoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PsychoLabel = styled.span`
  font-size: 10px;
  color: #94a3b8;
  width: 72px;
  text-align: ${props => props.$align || 'right'};
  flex-shrink: 0;
`;

/* ─── Building Profile ─── */

const BuildingBanner = styled.div`
  padding: 12px;
  background: #e8f0fe;
  border-radius: ${DesignTokens.borderRadius.sm};
  border-left: 3px solid #4285F4;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 16px;

  strong {
    color: #1A73E8;
  }
`;

/* ═══════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════ */

const TasteProfileCard = ({ userId }) => {
  const [profile, setProfile] = useState(null);
  const [customDimensions, setCustomDimensions] = useState(null);
  const [surpriseMe, setSurpriseMe] = useState(false);

  const [dnaEvolution, setDnaEvolution] = useState(null);

  // Load from localStorage (API endpoint will be wired later)
  useEffect(() => {
    try {
      const stored = localStorage.getItem('travelDNA');
      if (stored) {
        const data = JSON.parse(stored);
        const dims = data.dimensions || {};
        setProfile({
          dimensions: dims,
          personality: data.personality || 'The Curious Wanderer',
          psychographic: data.psychographic || null,
          travelSoul: data.travelSoul || null,
          lovedTypes: ['Street food', 'Walking tours', 'Morning activities'],
          avoidedTypes: ['Crowded tourist traps', 'Long bus rides'],
          feedbackCount: 3,
        });

        // DNA History tracking
        try {
          const historyRaw = localStorage.getItem('travelDNAHistory');
          const history = historyRaw ? JSON.parse(historyRaw) : [];
          const lastEntry = history[history.length - 1];
          const dimsStr = JSON.stringify(dims);
          if (!lastEntry || JSON.stringify(lastEntry.dimensions) !== dimsStr) {
            history.push({ date: new Date().toISOString(), dimensions: dims, source: 'load' });
            if (history.length > 10) history.splice(0, history.length - 10);
            localStorage.setItem('travelDNAHistory', JSON.stringify(history));
          }
          // Compute evolution for display
          if (history.length >= 2) {
            const first = history[0].dimensions;
            const last = history[history.length - 1].dimensions;
            const changes = [];
            Object.keys(last).forEach(key => {
              const oldVal = Math.round((first[key] || 0) * 100);
              const newVal = Math.round((last[key] || 0) * 100);
              if (Math.abs(newVal - oldVal) >= 5) {
                const dim = DIMENSIONS.find(d => d.key === key);
                const arrow = newVal > oldVal ? '\u2191' : '\u2193';
                changes.push(`${dim?.label || key}: ${oldVal} ${arrow} ${newVal}`);
              }
            });
            if (changes.length > 0) setDnaEvolution(changes);
          }
        } catch { /* ignore */ }
      }
    } catch {
      // ignore
    }
  }, [userId]);

  const activeDimensions = customDimensions || profile?.dimensions || {}; // eslint-disable-line react-hooks/exhaustive-deps
  const isCustomized = customDimensions !== null;

  const handleDimensionClick = useCallback((key, e) => {
    const track = e.currentTarget;
    const rect = track.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const newValue = Math.round((x / rect.width) * 100) / 100;
    setCustomDimensions(prev => ({
      ...(prev || profile?.dimensions || {}),
      [key]: Math.max(0, Math.min(1, newValue)),
    }));
  }, [profile]);

  const handleReset = useCallback(() => {
    setCustomDimensions(null);
  }, []);

  const radarPoints = useMemo(() => {
    return DIMENSIONS.map((dim, i) => {
      const angle = (360 / DIMENSIONS.length) * i;
      const value = activeDimensions[dim.key] || 0;
      return polarToCartesian(angle, RADIUS * value);
    });
  }, [activeDimensions]);

  const polygonPoints = radarPoints.map(p => `${p.x},${p.y}`).join(' ');

  if (!profile) {
    return (
      <Card>
        <CardTitle>Your Travel DNA</CardTitle>
        <CardSubtitle>Generate an itinerary or take the Travel DNA quiz to build your taste profile.</CardSubtitle>
        <BuildingBanner>
          <strong>No profile yet.</strong> Start by creating an itinerary or completing the Travel DNA mood board on the home page.
        </BuildingBanner>
      </Card>
    );
  }

  return (
    <Card
      data-travel-dna-card
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <CardTitle>Your Travel DNA</CardTitle>
      <CardSubtitle>Based on your trips and preferences</CardSubtitle>

      {profile.feedbackCount < 5 && (
        <BuildingBanner>
          Building your profile... <strong>{5 - profile.feedbackCount} more trips</strong> to unlock full personalization.
        </BuildingBanner>
      )}

      <PersonalityBadge>
        <PersonalityLabel>{profile.personality}</PersonalityLabel>
      </PersonalityBadge>

      {profile.travelSoul && TRAVEL_SOUL_LABELS[profile.travelSoul] && (
        <TravelSoulBadge>
          <TravelSoulIcon>🧭</TravelSoulIcon>
          <TravelSoulText>
            <TravelSoulName>{TRAVEL_SOUL_LABELS[profile.travelSoul].label}</TravelSoulName>
            <TravelSoulDesc>{TRAVEL_SOUL_LABELS[profile.travelSoul].description}</TravelSoulDesc>
          </TravelSoulText>
        </TravelSoulBadge>
      )}

      <ChartContainer>
        <svg width={CHART_SIZE} height={CHART_SIZE} viewBox={`0 0 ${CHART_SIZE} ${CHART_SIZE}`}>
          {Array.from({ length: RINGS }, (_, i) => {
            const r = (RADIUS / RINGS) * (i + 1);
            const ringPts = DIMENSIONS.map((_, j) => {
              const angle = (360 / DIMENSIONS.length) * j;
              return polarToCartesian(angle, r);
            });
            return (
              <polygon
                key={i}
                points={ringPts.map(p => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke="#e2e8f0"
                strokeWidth="1"
              />
            );
          })}

          {DIMENSIONS.map((_, i) => {
            const angle = (360 / DIMENSIONS.length) * i;
            const end = polarToCartesian(angle, RADIUS);
            return <line key={i} x1={CENTER} y1={CENTER} x2={end.x} y2={end.y} stroke="#e2e8f0" strokeWidth="1" />;
          })}

          <motion.polygon
            points={polygonPoints}
            fill="rgba(66, 133, 244, 0.15)"
            stroke="#4285F4"
            strokeWidth="2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            style={{ transformOrigin: `${CENTER}px ${CENTER}px` }}
          />

          {radarPoints.map((p, i) => (
            <motion.circle
              key={i}
              cx={p.x}
              cy={p.y}
              r="3.5"
              fill="#4285F4"
              stroke="white"
              strokeWidth="1.5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 + i * 0.08 }}
            />
          ))}

          {DIMENSIONS.map((dim, i) => {
            const angle = (360 / DIMENSIONS.length) * i;
            const pos = polarToCartesian(angle, RADIUS + 20);
            return (
              <text
                key={dim.key}
                x={pos.x}
                y={pos.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#64748b"
                fontSize="10"
                fontWeight="500"
              >
                {dim.label}
              </text>
            );
          })}
        </svg>
      </ChartContainer>

      <DimensionList>
        {DIMENSIONS.map((dim, i) => {
          const value = activeDimensions[dim.key] || 0;
          return (
            <DimRow key={dim.key}>
              <DimIcon>{dim.icon}</DimIcon>
              <DimLabel>{dim.label}</DimLabel>
              <DimTrack
                $editable
                onClick={(e) => handleDimensionClick(dim.key, e)}
              >
                <DimFill
                  initial={{ width: 0 }}
                  animate={{ width: `${value * 100}%` }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                />
              </DimTrack>
              <DimValue>{Math.round(value * 100)}%</DimValue>
            </DimRow>
          );
        })}
      </DimensionList>

      {dnaEvolution && dnaEvolution.length > 0 && (
        <BuildingBanner style={{ borderLeftColor: '#16A34A' }}>
          <strong>Your profile is evolving:</strong> {dnaEvolution.join(' | ')}
        </BuildingBanner>
      )}

      {profile.psychographic && (
        <PsychographicSection>
          <SectionLabel>Travel Style Spectrum</SectionLabel>
          {PSYCHOGRAPHIC_DIMENSIONS.map((dim, i) => {
            const value = profile.psychographic[dim.key] || 50;
            return (
              <PsychoRow key={dim.key}>
                <PsychoLabel>{dim.labelLow}</PsychoLabel>
                <DimTrack>
                  <DimFill
                    initial={{ width: 0 }}
                    animate={{ width: `${value}%` }}
                    transition={{ delay: i * 0.08, duration: 0.5 }}
                  />
                </DimTrack>
                <PsychoLabel $align="left">{dim.labelHigh}</PsychoLabel>
              </PsychoRow>
            );
          })}
        </PsychographicSection>
      )}

      <LearnedSection>
        <SectionLabel>Loved</SectionLabel>
        <div>
          {(profile.lovedTypes || []).map((type, i) => (
            <PreferenceChip key={i}>{type}</PreferenceChip>
          ))}
        </div>
        {profile.avoidedTypes && profile.avoidedTypes.length > 0 && (
          <>
            <SectionLabel style={{ marginTop: '12px' }}>Tends to avoid</SectionLabel>
            <div>
              {profile.avoidedTypes.map((type, i) => (
                <AvoidChip key={i}>{type}</AvoidChip>
              ))}
            </div>
          </>
        )}
      </LearnedSection>

      <ControlsRow>
        <ResetBtn onClick={handleReset} disabled={!isCustomized}>
          {isCustomized ? 'Reset to learned' : 'No changes'}
        </ResetBtn>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <ResetBtn
            onClick={async () => {
              try {
                const { default: html2canvas } = await import('html2canvas');
                const cardEl = document.querySelector('[data-travel-dna-card]');
                if (!cardEl) return;
                const canvas = await html2canvas(cardEl, { backgroundColor: '#ffffff', scale: 2 });
                const link = document.createElement('a');
                link.download = `travel-dna-${(profile.personality || 'profile').replace(/\s+/g, '-').toLowerCase()}.png`;
                link.href = canvas.toDataURL();
                link.click();
              } catch {
                // html2canvas not available — fallback to clipboard
                try {
                  await navigator.clipboard.writeText(`My Travel DNA: ${profile.personality}`);
                } catch { /* ignore */ }
              }
            }}
          >
            Share DNA
          </ResetBtn>
          <SurpriseToggle onClick={() => setSurpriseMe(!surpriseMe)}>
            <ToggleSwitch $on={surpriseMe} />
            Surprise me
          </SurpriseToggle>
        </div>
      </ControlsRow>
    </Card>
  );
};

export default TasteProfileCard;
